import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check, CheckCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/** Bell-icon met unread-badge + dropdown met laatste 20 in-app meldingen.
 *  Refresht elke 30s + na close/navigate.
 */
export default function MeldingenCentrum({ variant = "desktop" }) {
  const [open, setOpen] = useState(false);
  const [meldingen, setMeldingen] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  // Persist last-selected chip in localStorage (klantwens 31-07-2026)
  const [moduleFilter, setModuleFilter] = useState(() => {
    try { return localStorage.getItem("meldingen.moduleFilter") || "all"; }
    catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("meldingen.moduleFilter", moduleFilter); } catch {}
  }, [moduleFilter]);
  const boxRef = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/meldingen-centrum?limit=20");
      setMeldingen(data.meldingen || []);
      setUnread(data.unread_count || 0);
    } catch { /* stil */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  // Klik-buiten sluit
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/meldingen-centrum/mark-all-read");
      setMeldingen((prev) => prev.map((m) => ({ ...m, read: true })));
      setUnread(0);
    } catch (e) {
      toast.error("Kon niet als gelezen markeren");
    }
  };

  const clickMelding = async (m) => {
    if (!m.read && m._id) {
      try {
        await api.post(`/notifications/meldingen-centrum/${m._id}/mark-read`);
      } catch { /* stil */ }
      setMeldingen((prev) => prev.map((x) => (x._id === m._id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (m.url) {
      nav(m.url);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "net";
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}u`;
    return `${Math.floor(s / 86400)}d`;
  };

  // Chip-filters + counts (afgeleid van meldingen-lijst)
  const MODULE_CHIPS = [
    { key: "all", label: "Alle" },
    { key: "tickets", label: "Tickets" },
    { key: "verlof", label: "Verlof" },
    { key: "commissie", label: "Commissie" },
    { key: "bb", label: "BB" },
  ];
  const perModuleCount = (mod) => {
    if (mod === "all") return meldingen.length;
    return meldingen.filter((m) => (m.module || "algemeen").toLowerCase().includes(mod)).length;
  };
  const gefilterdeMeldingen = moduleFilter === "all"
    ? meldingen
    : meldingen.filter((m) => (m.module || "algemeen").toLowerCase().includes(moduleFilter));

  return (
    <div className="relative" ref={boxRef} data-testid={`meldingen-centrum-${variant}`}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 hover:text-slate-900 transition"
        aria-label="Meldingen"
        data-testid={`meldingen-bell-btn-${variant}`}
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1"
            data-testid={`meldingen-badge-${variant}`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl border border-border shadow-xl z-50 max-h-[80vh] flex flex-col"
          data-testid="meldingen-dropdown"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-indigo-600" />
              <div className="font-semibold text-sm">Meldingen</div>
              {unread > 0 && (
                <Badge className="bg-red-100 text-red-700 text-[10px]">{unread} nieuw</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <Button
                  variant="ghost" size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={markAllRead}
                  data-testid="mark-all-read-btn"
                  title="Alles als gelezen"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button
                variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => setOpen(false)}
                aria-label="Sluiten"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Module-filter chips */}
          <div className="px-3 py-2 border-b border-border flex items-center gap-1 overflow-x-auto scroll-momentum" data-testid="meldingen-chips">
            {MODULE_CHIPS.map((c) => {
              const active = moduleFilter === c.key;
              const count = perModuleCount(c.key);
              return (
                <button
                  key={c.key}
                  onClick={() => setModuleFilter(c.key)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition whitespace-nowrap ${
                    active
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
                  }`}
                  data-testid={`meldingen-chip-${c.key}`}
                >
                  {c.label}
                  <span className={`ml-1 text-[10px] font-mono ${active ? "text-white/80" : "text-slate-400"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="overflow-y-auto flex-1">
            {loading && meldingen.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">Laden…</div>
            ) : gefilterdeMeldingen.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                {meldingen.length === 0
                  ? "Geen meldingen"
                  : `Geen meldingen in "${MODULE_CHIPS.find(c => c.key === moduleFilter)?.label || moduleFilter}"`}
              </div>
            ) : (
              <ul className="divide-y divide-border" data-testid="meldingen-lijst">
                {gefilterdeMeldingen.map((m) => (
                  <li
                    key={m._id || m.created_at}
                    className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition ${
                      !m.read ? "bg-blue-50/50" : ""
                    }`}
                    onClick={() => clickMelding(m)}
                    data-testid={`melding-item${!m.read ? "-unread" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                        !m.read ? "bg-blue-500" : "bg-transparent"
                      }`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className={`text-sm font-medium truncate ${!m.read ? "text-slate-900" : "text-slate-700"}`}>
                            {m.title}
                          </div>
                          <div className="text-[10px] text-slate-400 shrink-0">{timeAgo(m.created_at)}</div>
                        </div>
                        <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{m.body}</div>
                        <div className="flex items-center gap-1 mt-1">
                          <Badge variant="secondary" className="text-[9px] px-1.5 py-0">{m.module || "algemeen"}</Badge>
                          {m.url && (
                            <span className="text-[10px] text-indigo-600">→ open</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-border px-4 py-2 text-center">
            <button
              onClick={() => { setOpen(false); nav("/mijn-gegevens"); }}
              className="text-xs text-slate-500 hover:text-slate-900"
              data-testid="meldingen-voorkeuren-link"
            >
              Meldingen-voorkeuren →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
